import {
  Dispatch,
  ReactNode,
  SetStateAction,
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";
import { isMobile } from "react-device-detect";
import ViewDialog from "../components/modals/ViewDialog";
import { useOrganizationContext } from "./OrganizationContext";
import { useSessionContext } from "./SessionContext";
import { useNavigate } from "react-router-dom";
import { isPatientQuestionnaireLinkFlowActive } from "src-next/shared/patientQuestionnaireLink";

interface ViewContextValue {
  sidebarIsOpen: boolean;
  setSidebarIsOpen: Dispatch<SetStateAction<boolean>>;
  viewDialogIsOpen: boolean;
  setViewDialogIsOpen: Dispatch<SetStateAction<boolean>>;
  changeView: Function;
  availableRoles: any;
  viewType: string;
  setViewType: any;
}

export const ViewContext = createContext<ViewContextValue>(null!);

export function ViewContextProvider({ children }: { children: ReactNode }) {
  const { access, roles, setAccess, getRolesArray, loading } =
    useSessionContext();
  const { refetchOrganization, resetStates } = useOrganizationContext();

  const [sidebarIsOpen, setSidebarIsOpen] = useState<boolean>(
    isMobile ? false : true
  );
  const [viewDialogIsOpen, setViewDialogIsOpen] = useState<boolean>(false);
  const { person } = useSessionContext();
  const navigate = useNavigate();

  const selectedViewType = localStorage.getItem("viewType") as "admin" | "user";

  const [viewType, setViewType] = useState<"admin" | "user">(
    selectedViewType || "user"
  );

  // Monitora quando os roles são carregados inicialmente
  useEffect(() => {
    if (roles && roles.length > 0) {
      if (!access?.type && !isMobile) {
        // Fluxo por link de questionário: força visão paciente (sem escolha de clínica).
        if (isPatientQuestionnaireLinkFlowActive()) {
          setAccess({ type: "patient" });
          return;
        }
        setViewDialogIsOpen(true);
      }
    }
  }, [roles, access?.type, setAccess]);

  useEffect(() => {
    if (access && !loading) {
      // Em mobile, força tipo patient se não houver tipo definido
      if (isMobile && !access.type) {
        setAccess({ type: "patient" });
        return;
      }

      // Se for professional e tiver roles, atualiza a visão
      if (access?.type === "professional" && access.roles?.length) {
        changeVisionDashboard();
        refetchOrganization();
        resetStates();
      }
    }
  }, [access, loading]);

  function changeVisionDashboard() {
    if (selectedViewType) return;
    if (access?.roles?.includes("practitioner")) {
      setViewType("user");
      localStorage.setItem("viewType", "user");
      return;
    } else if (access?.roles?.includes("manager")) {
      setViewType("admin");
      localStorage.setItem("viewType", "admin");
      return;
    }
  }

  function changeView() {
    // Se não tiver roles, define como patient
    if (!roles || roles.length === 0) {
      setAccess({ type: "patient" });
      navigate("/");
      return;
    }

    // Se tiver apenas um role, seleciona automaticamente
    if (roles.length === 1) {
      setAccess({
        type: "professional",
        organizationId: roles[0]?.organization?.reference?.split("-")[1],
        roles: getRolesArray(roles[0]),
      });
      navigate("/");
      return;
    }

    // Se tiver múltiplos roles, abre o modal
    localStorage.setItem(
      "organizationId",
      roles[0]?.organization?.reference?.split("-")[1]
    );
    setViewDialogIsOpen(true);
  }

  const contextValue = {
    sidebarIsOpen,
    setSidebarIsOpen,
    viewDialogIsOpen,
    setViewDialogIsOpen,
    changeView,
    availableRoles: access?.roles,
    viewType,
    setViewType,
  };
  return (
    <ViewContext.Provider value={contextValue}>
      {person?.active && <ViewDialog />}
      {children}
    </ViewContext.Provider>
  );
}

export function useViewContext() {
  return useContext(ViewContext);
}
